import React, { useState, useEffect } from "react";
import {
  TextField,
  Button,
  Switch,
  FormControlLabel,
  Box,
  Typography,
  Divider,
  Grid,
  Paper,
  Alert,
} from "@mui/material";
import { useClientInfo, axiosInstance } from "../../utils/commonImports";

export default function EmailSettings() {
  const { clientId } = useClientInfo();

  const [form, setForm] = useState({
    smtp_host: "",
    smtp_port: 587,
    smtp_user: "",
    smtp_pass: "",
    use_ssl: false,
    from_name: "",
    reply_to: "",
    is_active: true,
  });

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState("");

  // ---------------------------------------------------------
  // LOAD EMAIL SETTINGS
  // ---------------------------------------------------------
  useEffect(() => {
    const loadSettings = async () => {
      try {
        const res = await axiosInstance.get(
          `/api/email-settings/${clientId}`
        );

        if (res.data.settings) {
          setForm({
            smtp_host: res.data.settings.smtp_host || "",
            smtp_port: res.data.settings.smtp_port || 587,
            smtp_user: res.data.settings.smtp_user || "",
            smtp_pass: "", // Never show existing password
            use_ssl: Boolean(res.data.settings.use_ssl),
            from_name: res.data.settings.from_name || "",
            reply_to: res.data.settings.reply_to || "",
            is_active:
              res.data.settings.is_active !== undefined
                ? Boolean(res.data.settings.is_active)
                : true,
          });
        }
      } catch (err) {
        console.log("No existing email settings.");
      } finally {
        setLoading(false);
      }
    };

    if (clientId) {
      loadSettings();
    }
  }, [clientId]);

  // ---------------------------------------------------------
  // HANDLE FIELD CHANGE
  // ---------------------------------------------------------
  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;

    setForm((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  // ---------------------------------------------------------
  // SAVE SETTINGS
  // ---------------------------------------------------------
  const handleSave = async () => {
    setMessage("");

    // Basic validation
    if (!form.smtp_host.trim()) {
      setMessage("Please enter the SMTP Host.");
      return;
    }

    if (!form.smtp_port) {
      setMessage("Please enter the SMTP Port.");
      return;
    }

    if (!form.smtp_user.trim()) {
      setMessage("Please enter the SMTP Username.");
      return;
    }

    setSaving(true);

    try {
      const res = await axiosInstance.post(
        `/api/email-settings/${clientId}`,
        {
          ...form,
          smtp_port: Number(form.smtp_port),
        }
      );

      setMessage(res.data.message || "Email settings saved successfully.");

      // Clear password after successful save
      setForm((prev) => ({
        ...prev,
        smtp_pass: "",
      }));
    } catch (err) {
      console.error(err);

      setMessage(
        err?.response?.data?.message ||
          "Failed to save email settings."
      );
    } finally {
      setSaving(false);
    }
  };

  // ---------------------------------------------------------
  // LOADING
  // ---------------------------------------------------------
  if (loading) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          minHeight: 250,
        }}
      >
        <Typography color="text.secondary">
          Loading email settings...
        </Typography>
      </Box>
    );
  }

  // ---------------------------------------------------------
  // UI
  // ---------------------------------------------------------
  return (
    <Box
      sx={{
        width: "100%",
        maxWidth: 850,
        mx: "auto",
        mt: 3,
        mb: 4,
        px: { xs: 1, sm: 2 },
      }}
    >
      <Paper
        elevation={3}
        sx={{
          borderRadius: 2,
          overflow: "hidden",
        }}
      >
        {/* =====================================================
            HEADER
        ====================================================== */}
        <Box
          sx={{
            px: { xs: 2, sm: 3 },
            py: 2.5,
            backgroundColor: "#f8f9fa",
          }}
        >
          <Typography
            variant="h5"
            sx={{
              fontWeight: 600,
              color: "#222",
            }}
          >
            Email Settings
          </Typography>

          <Typography
            variant="body2"
            sx={{
              mt: 0.5,
              color: "text.secondary",
            }}
          >
            Configure your SMTP server to send emails from the application.
          </Typography>
        </Box>

        <Divider />

        {/* =====================================================
            FORM
        ====================================================== */}
        <Box sx={{ p: { xs: 2, sm: 3 } }}>
          {/* ===================================================
              SUCCESS / ERROR MESSAGE
          ==================================================== */}
          {message && (
            <Alert
              severity={
                message.toLowerCase().includes("failed") ||
                message.toLowerCase().includes("please")
                  ? "error"
                  : "success"
              }
              sx={{ mb: 3 }}
              onClose={() => setMessage("")}
            >
              {message}
            </Alert>
          )}

          {/* ===================================================
              SECTION 1 - SMTP SERVER
          ==================================================== */}
          <Typography
            variant="subtitle1"
            sx={{
              fontWeight: 600,
              mb: 0.5,
            }}
          >
            SMTP Server
          </Typography>

          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ mb: 2 }}
          >
            Enter the mail server details provided by your email provider.
          </Typography>

          <Grid container spacing={2}>
            {/* SMTP HOST */}
            <Grid item xs={12} sm={8}>
              <TextField
                fullWidth
                label="SMTP Host"
                name="smtp_host"
                value={form.smtp_host}
                onChange={handleChange}
                required
                placeholder="smtp.gmail.com"
                helperText="Example: smtp.gmail.com"
              />
            </Grid>

            {/* SMTP PORT */}
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                label="SMTP Port"
                name="smtp_port"
                type="number"
                value={form.smtp_port}
                onChange={handleChange}
                required
                inputProps={{
                  min: 1,
                  max: 65535,
                }}
                helperText={
                  form.use_ssl
                    ? "Usually 465"
                    : "Usually 587"
                }
              />
            </Grid>

            {/* SSL */}
            <Grid item xs={12}>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  p: 1.5,
                  border: "1px solid",
                  borderColor: "divider",
                  borderRadius: 1,
                  backgroundColor: "#fafafa",
                }}
              >
                <FormControlLabel
                  control={
                    <Switch
                      checked={form.use_ssl}
                      name="use_ssl"
                      onChange={handleChange}
                    />
                  }
                  label={
                    <Box>
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>
                        Use SSL / TLS
                      </Typography>

                      <Typography
                        variant="caption"
                        color="text.secondary"
                      >
                        {form.use_ssl
                          ? "SSL enabled — commonly used with port 465"
                          : "SSL disabled — commonly used with port 587"}
                      </Typography>
                    </Box>
                  }
                />
              </Box>
            </Grid>
          </Grid>

          {/* ===================================================
              DIVIDER
          ==================================================== */}
          <Divider sx={{ my: 3 }} />

          {/* ===================================================
              SECTION 2 - AUTHENTICATION
          ==================================================== */}
          <Typography
            variant="subtitle1"
            sx={{
              fontWeight: 600,
              mb: 0.5,
            }}
          >
            SMTP Authentication
          </Typography>

          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ mb: 2 }}
          >
            Enter the username and password used to authenticate with
            your email server.
          </Typography>

          <Grid container spacing={2}>
            {/* USERNAME */}
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="SMTP Username"
                name="smtp_user"
                value={form.smtp_user}
                onChange={handleChange}
                required
                placeholder="your@email.com"
                autoComplete="off"
              />
            </Grid>

            {/* PASSWORD */}
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="SMTP Password"
                name="smtp_pass"
                type="password"
                value={form.smtp_pass}
                onChange={handleChange}
                placeholder="Enter new password"
                autoComplete="new-password"
                helperText="Leave blank to keep the existing password."
              />
            </Grid>
          </Grid>

          {/* ===================================================
              DIVIDER
          ==================================================== */}
          <Divider sx={{ my: 3 }} />

          {/* ===================================================
              SECTION 3 - SENDER DETAILS
          ==================================================== */}
          <Typography
            variant="subtitle1"
            sx={{
              fontWeight: 600,
              mb: 0.5,
            }}
          >
            Sender Details
          </Typography>

          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ mb: 2 }}
          >
            Configure how the sender information appears in outgoing emails.
          </Typography>

          <Grid container spacing={2}>
            {/* FROM NAME */}
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="From Name"
                name="from_name"
                value={form.from_name}
                onChange={handleChange}
                placeholder="WheelsERP"
                helperText="Name shown to the recipient."
              />
            </Grid>

            {/* REPLY TO */}
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Reply To"
                name="reply_to"
                type="email"
                value={form.reply_to}
                onChange={handleChange}
                placeholder="support@example.com"
                helperText="Optional reply-to email address."
              />
            </Grid>
          </Grid>

          {/* ===================================================
              DIVIDER
          ==================================================== */}
          <Divider sx={{ my: 3 }} />

          {/* ===================================================
              SECTION 4 - STATUS
          ==================================================== */}
          <Typography
            variant="subtitle1"
            sx={{
              fontWeight: 600,
              mb: 0.5,
            }}
          >
            Email Service Status
          </Typography>

          <Box
            sx={{
              mt: 1.5,
              p: 2,
              border: "1px solid",
              borderColor: "divider",
              borderRadius: 1,
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              gap: 2,
              backgroundColor: "#fafafa",
            }}
          >
            <Box>
              <Typography
                variant="body1"
                sx={{ fontWeight: 500 }}
              >
                Email Sending
              </Typography>

              <Typography
                variant="body2"
                color="text.secondary"
              >
                {form.is_active
                  ? "Email sending is enabled."
                  : "Email sending is currently disabled."}
              </Typography>
            </Box>

            <FormControlLabel
              sx={{ mr: 0 }}
              control={
                <Switch
                  checked={form.is_active}
                  name="is_active"
                  onChange={handleChange}
                />
              }
              label={form.is_active ? "Active" : "Inactive"}
            />
          </Box>

          {/* ===================================================
              SAVE BUTTON
          ==================================================== */}
          <Box
            sx={{
              mt: 3,
              display: "flex",
              justifyContent: "flex-end",
            }}
          >
            <Button
              variant="contained"
              color="primary"
              onClick={handleSave}
              disabled={saving}
              sx={{
                minWidth: 160,
                height: 44,
                textTransform: "none",
                fontWeight: 600,
              }}
            >
              {saving ? "Saving..." : "Save Settings"}
            </Button>
          </Box>
        </Box>
      </Paper>
    </Box>
  );
}